// Liefde Wen product page behaviour: option pill selection -> variant
// resolution, price/button state updates, and gallery thumbnail switching.
// Works alongside Horizon's <product-form-component>, which handles the
// actual add-to-cart submit and cart drawer refresh.

class LwProduct extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-lw-variants]');
    this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
    this.optionPills = Array.from(this.querySelectorAll('[data-lw-option-pill]'));
    this.variantInput = this.querySelector('input[name="id"]');
    this.priceEl = this.querySelector('[data-lw-price]');
    this.addButton = this.querySelector('[data-lw-add]');
    this.addButtonLabel = this.querySelector('[data-lw-add-label]');
    this.mainImage = this.querySelector('[data-lw-main-image]');
    this.thumbs = Array.from(this.querySelectorAll('[data-lw-thumb]'));

    this.defaultAddLabel = this.addButtonLabel?.textContent.trim() || 'Add to bag';

    this.optionPills.forEach((pill) => {
      pill.addEventListener('click', () => this.selectOption(pill));
    });

    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.showImage(index));
    });
  }

  get selectedOptions() {
    const options = [];
    for (const pill of this.optionPills) {
      if (pill.classList.contains('is-active')) {
        options[Number(pill.dataset.optionIndex)] = pill.dataset.optionValue;
      }
    }
    return options;
  }

  selectOption(pill) {
    const index = pill.dataset.optionIndex;
    this.optionPills
      .filter((p) => p.dataset.optionIndex === index)
      .forEach((p) => {
        p.classList.toggle('is-active', p === pill);
        p.setAttribute('aria-pressed', String(p === pill));
      });
    this.updateVariant();
  }

  updateVariant() {
    const selected = this.selectedOptions;
    const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));

    if (!variant) {
      this.setAddState(false, this.addButton?.dataset.unavailableLabel || 'Unavailable');
      return;
    }

    if (this.variantInput) this.variantInput.value = variant.id;
    if (this.priceEl) this.priceEl.textContent = variant.price_formatted;

    if (variant.available) {
      this.setAddState(true, this.defaultAddLabel);
    } else {
      this.setAddState(false, this.addButton?.dataset.soldOutLabel || 'Sold out');
    }

    if (variant.featured_media_index != null) {
      this.showImage(variant.featured_media_index);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  setAddState(enabled, label) {
    if (!this.addButton) return;
    this.addButton.disabled = !enabled;
    if (this.addButtonLabel) this.addButtonLabel.textContent = label;
  }

  showImage(index) {
    const thumb = this.thumbs[index];
    if (!thumb || !this.mainImage) return;

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.mainImage.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 250, easing: 'ease-out' });
    }
    this.mainImage.src = thumb.dataset.fullSrc;
    if (thumb.dataset.fullSrcset) this.mainImage.srcset = thumb.dataset.fullSrcset;
    if (thumb.dataset.alt != null) this.mainImage.alt = thumb.dataset.alt;

    this.thumbs.forEach((t, i) => {
      t.classList.toggle('is-active', i === index);
      t.setAttribute('aria-current', String(i === index));
    });
  }
}

if (!customElements.get('lw-product')) {
  customElements.define('lw-product', LwProduct);
}
